import React, { useEffect } from 'react';

import type { KeyPhraseNodeType } from '../../api/useAutohighlightSupplier';
import type { DefinitionConfigType, DefinitionSourceConfigType } from '../types';

import { DefinitionTextHighlighter } from './definition-text-highlighter';

interface AkRendererProps {
	match: string;
	marks: Set<string>;
	startPos: number;
}

type DefinitionTextHighlighterProps = AkRendererProps & {
	definitionConfig: DefinitionConfigType;
} & { sourceConfig: DefinitionSourceConfigType } & {
	keyPhrases: KeyPhraseNodeType[];
};

const existingHighlightedTextMap = new Map();

export const RendererDefinitionTextHighlighter = ({
	children,
	match,
	marks,
	startPos,
	definitionConfig,
	sourceConfig,
	keyPhrases,
}: React.PropsWithChildren<DefinitionTextHighlighterProps>): React.JSX.Element => {
	const contentARI = sourceConfig.contentARI;
	if (!existingHighlightedTextMap.has(`${contentARI}-${match}`)) {
		existingHighlightedTextMap.set(`${contentARI}-${match}`, startPos);
	}
	useEffect(() => {
		return () => {
			existingHighlightedTextMap.delete(`${contentARI}-${match}`);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// no comments or links should be highlighted
	if (!(marks.has('annotation') || marks.has('link'))) {
		const nodeMatch = keyPhrases.find((node: KeyPhraseNodeType) => node.keyPhrase === match);
		// only highlight first occurrence of the word
		if (!!nodeMatch && existingHighlightedTextMap.get(`${contentARI}-${match}`) === startPos) {
			return (
				<DefinitionTextHighlighter
					keyPhraseCategory={nodeMatch.category}
					definitionConfig={definitionConfig}
					sourceConfig={sourceConfig}
				>
					{children}
				</DefinitionTextHighlighter>
			);
		}
	}

	return <>{children}</>;
};
